'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface ExpertiseSection {
  id: string;
  leadText?: string;
  title: string;
  description: string[];
  buttonLabel: string;
  image: string;
  imageAlt: string;
  bgVariant: 'white' | 'orange' | 'teal';
  reversed: boolean;
}

const expertiseSections: ExpertiseSection[] = [
{
  id: 'development',
  title: 'Brickwell Development',
  description: [
  'We bring decades of experience and expertise to every facet of the design-build process to successfully craft viable and financially successful housing solutions. We collaborate with cities, rural areas, universities, and community organizations to develop creative solutions to housing challenges — building comfortable living spaces that are knitted into the fabric of the community where residents can thrive.',
  'We maximize job creation and embrace environmentally sound practices, leading in green building techniques for lasting operational savings and sustainable communities.'],

  buttonLabel: 'Connect With Our Development Team',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0547e0e-1779936772255.png",
  imageAlt: 'Brickwell Housing development team reviewing architectural blueprints for a new affordable housing community',
  bgVariant: 'white',
  reversed: false
},
{
  id: 'management',
  title: 'Brickwell Management',
  description: [
  'Once we build a community, we don\'t like to leave. At Brickwell, it\'s all about the residents, and there\'s no better way to lift their lives than to be a part of those lives moving forward. Our management company and supportive resident services teams win industry awards because they know who they\'re really working for.',
  'We are also the trusted fee management partner of other private and nonprofit owners, bringing our award-winning standards to communities across the country.'],

  buttonLabel: 'Connect With Our Management Team',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1489aebe8-1772201580780.png",
  imageAlt: 'Brickwell Housing property manager greeting residents at a well-maintained apartment community',
  bgVariant: 'orange',
  reversed: false
},
{
  id: 'construction',
  title: 'Construction',
  description: [
  'Whether it\'s a gut rehab or out-of-the-ground residential construction, we deliver the highest quality at the lowest deliverable cost. We provide expertise in all aspects of design-build, plan review, and pre-construction services as well as conceptual budgeting and estimating, general contracting and construction management.',
  'Our Brickwell team, along with our client partners and host of well-vetted vendors, subcontractors and suppliers, works with unified effort to ensure that every detail contributes to the health, happiness and potential of our residents.'],

  buttonLabel: 'Connect With Our Construction Team',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_195017ff3-1772202547308.png",
  imageAlt: 'Brickwell Housing construction crew working on a new residential building with safety equipment and scaffolding',
  bgVariant: 'white',
  reversed: true
},
{
  id: 'investment',
  leadText: 'Investments',
  title: 'Acquisitions and Capital Solutions',
  description: [
  'Our goal is to lift as many lives as possible through smart, measured growth. We are always looking for affordable, attainable, senior, and market-rate assets that can be reinvigorated for added value and community impact.'],

  buttonLabel: 'Connect With Our Acquisition and Capital Solutions Teams',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19a9e3c1a-1779936770590.png",
  imageAlt: 'Modern residential building representing Brickwell Housing investment and acquisition portfolio',
  bgVariant: 'teal',
  reversed: true
},
{
  id: 'investment-management',
  title: 'Investment Management',
  description: [
  'For more than 50 years, we have done the extraordinary by valuing the needs of all stakeholders — residents, teammates, and owners. Through proactive asset planning, forecasting, and recapitalization strategies, we are able to invest in both the today and the tomorrow.'],

  buttonLabel: 'Connect with our Investment Management Team',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_178a43e9f-1765901059807.png",
  imageAlt: 'Brickwell Housing investment management team analyzing portfolio performance and long-term asset strategies',
  bgVariant: 'teal',
  reversed: false
}];


function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
            (entry.target as HTMLElement).style.opacity = '1';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function RevealElement({ children, delay = 0 }: {children: React.ReactNode;delay?: number;}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.style.transform = 'translateY(80px)';
    el.style.opacity = '0';
    el.style.transition = `transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, opacity 0.8s ease ${delay}ms`;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
            (entry.target as HTMLElement).style.opacity = '1';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return <div ref={ref}>{children}</div>;
}

function ExpertiseSectionBlock({ section }: {section: ExpertiseSection;}) {
  const bgStyles: Record<string, React.CSSProperties> = {
    white: { backgroundColor: '#ffffff' },
    orange: { backgroundColor: '#f05323' },
    teal: { backgroundColor: '#00b195' }
  };

  const angleStyles: Record<string, React.CSSProperties> = {
    white: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '60px',
      background: '#f8f9fa',
      clipPath: 'polygon(0 100%, 100% 0, 100% 100%)',
      pointerEvents: 'none'
    },
    orange: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '60px',
      background: '#ffffff',
      clipPath: 'polygon(0 100%, 100% 0, 100% 100%)',
      pointerEvents: 'none'
    },
    teal: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '60px',
      background: '#f8f9fa',
      clipPath: 'polygon(0 100%, 100% 0, 100% 100%)',
      pointerEvents: 'none'
    }
  };

  const titleColor = section.bgVariant === 'white' ? '#003c82' : '#ffffff';
  const textColor = section.bgVariant === 'white' ? '#555555' : 'rgba(255,255,255,0.9)';
  const leadColor = section.bgVariant === 'white' ? '#f05323' : 'rgba(255,255,255,0.7)';

  return (
    <section
      id={section.id}
      style={{
        ...bgStyles[section.bgVariant],
        position: 'relative',
        paddingBottom: '80px'
      }}>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '80px 24px 0' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'center',
            direction: section.reversed ? 'rtl' : 'ltr'
          }}
          className="expertise-grid">

          {/* Image */}
          <RevealElement delay={0}>
            <div style={{ direction: 'ltr', overflow: 'hidden' }}>
              <img
                src={section.image}
                alt={section.imageAlt}
                style={{
                  width: '100%',
                  height: '460px',
                  objectFit: 'cover',
                  display: 'block',
                  transition: 'transform 0.6s ease'
                }}
                className="expertise-img" />

            </div>
          </RevealElement>

          {/* Content */}
          <RevealElement delay={150}>
            <div style={{ direction: 'ltr' }}>
              {section.leadText &&
              <strong
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '700',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: leadColor,
                  marginBottom: '12px'
                }}>

                  {section.leadText}
                </strong>
              }
              <h2
                style={{
                  fontSize: '36px',
                  fontWeight: '400',
                  color: titleColor,
                  lineHeight: 1.2,
                  marginBottom: '24px',
                  fontFamily: 'Montserrat, sans-serif'
                }}>

                {section.title}
              </h2>
              <div
                style={{
                  fontSize: '16px',
                  lineHeight: 1.8,
                  color: textColor,
                  marginBottom: '32px'
                }}>

                {section.description.map((para, i) =>
                <p key={i} style={{ marginBottom: i < section.description.length - 1 ? '16px' : '0' }}>
                    {para}
                  </p>
                )}
              </div>
              <div>
                <a
                  href="/contact"
                  className="expertise-btn"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: section.bgVariant === 'white' ? '#003c82' : '#ffffff',
                    color: section.bgVariant === 'white' ? '#ffffff' : '#003c82',
                    padding: '12px 24px',
                    fontSize: '13px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    letterSpacing: '0.02em',
                    fontFamily: 'Montserrat, sans-serif',
                    transition: 'background-color 0.2s ease, color 0.2s ease',
                    borderRadius: '2px'
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    if (section.bgVariant === 'white') {
                      el.style.backgroundColor = '#002a5c';
                    } else {
                      el.style.backgroundColor = 'rgba(255,255,255,0.85)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLAnchorElement;
                    if (section.bgVariant === 'white') {
                      el.style.backgroundColor = '#003c82';
                    } else {
                      el.style.backgroundColor = '#ffffff';
                    }
                  }}>

                  {section.buttonLabel}
                  <svg width="10" height="16" viewBox="0 0 21 32" fill="none">
                    <path
                      d="M3.556 1.778l14.222 14.222-14.222 14.222"
                      stroke="currentColor"
                      strokeWidth="4.75"
                      strokeLinecap="round"
                      strokeLinejoin="round" />

                  </svg>
                </a>
              </div>
            </div>
          </RevealElement>
        </div>
      </div>
      <div style={angleStyles[section.bgVariant]} />
    </section>);

}

export default function ExpertisePage() {
  return (
    <main style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <Header />

      {/* Interior Hero */}
      <section
        style={{
          position: 'relative',
          minHeight: '520px',
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          paddingTop: '80px'
        }}>

        {/* Background image */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
            "https://img.rocket.new/generatedImages/rocket_gen_img_15a456bbe-1767116732364.png",
            backgroundSize: 'cover',
            backgroundPosition: 'center 40%'
          }} />

        {/* Overlay */}
        <span
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(45deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 100%)'
          }} />

        {/* Cyan triangle decoration */}
        <span
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: 0,
            width: '45%',
            height: '120px',
            backgroundColor: '#6dcff6',
            clipPath: 'polygon(100% 0, 100% 100%, 0 100%)'
          }} />

        {/* Content */}
        <div
          style={{
            position: 'relative',
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 24px 80px',
            width: '100%'
          }}>

          <h1
            style={{
              fontSize: '54px',
              lineHeight: 1.1,
              color: '#ffffff',
              fontWeight: '400',
              margin: '0 0 24px',
              maxWidth: '600px',
              fontFamily: 'Montserrat, sans-serif'
            }}>

            Your Partner In Building The Extraordinary
          </h1>
          <p
            style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.9)',
              lineHeight: 1.6,
              margin: 0,
              maxWidth: '520px',
              fontFamily: 'Montserrat, sans-serif'
            }}>

            Building communities that transform lives requires passion, experience, and an amazing network of partners and teammates.
          </p>
        </div>
      </section>

      {/* Expertise Sections */}
      {expertiseSections.map((section) =>
      <ExpertiseSectionBlock key={section.id} section={section} />
      )}

      {/* Contact CTA Section */}
      <section
        style={{
          backgroundColor: '#f8f9fa',
          padding: '80px 0'
        }}>

        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 24px' }}>
          <div
            style={{
              backgroundColor: '#6dcff6',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              overflow: 'hidden'
            }}
            className="cta-grid">

            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1f7069e4d-1764661633094.png"
              alt="Brickwell Housing team collaborating on housing solutions in a modern office environment"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '400px',
                objectFit: 'cover',
                display: 'block'
              }} />

            <div
              style={{
                padding: '60px 50px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>

              <h2
                style={{
                  fontSize: '42px',
                  fontWeight: '400',
                  color: '#003c82',
                  marginBottom: '20px',
                  lineHeight: 1.1,
                  fontFamily: 'Montserrat, sans-serif'
                }}>

                Ready to build something{' '}
                <em style={{ fontStyle: 'italic' }}>extraordinary</em>?
              </h2>
              <p
                style={{
                  fontSize: '16px',
                  color: '#333333',
                  lineHeight: 1.7,
                  marginBottom: '32px'
                }}>

                Whether you\'re looking for a development partner, a trusted property manager, or a construction team that delivers — Brickwell Housing is ready to work with you.
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <a
                  href="/contact"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: '#003c82',
                    color: '#ffffff',
                    padding: '14px 28px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    borderRadius: '2px',
                    fontFamily: 'Montserrat, sans-serif',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#002a5c';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#003c82';}}>

                  Contact Us
                  <svg width="10" height="16" viewBox="0 0 21 32" fill="none">
                    <path d="M3.556 1.778l14.222 14.222-14.222 14.222" stroke="currentColor" strokeWidth="4.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
                <a
                  href="/portfolio"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: 'transparent',
                    color: '#003c82',
                    padding: '14px 28px',
                    fontSize: '14px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    borderRadius: '2px',
                    border: '2px solid #003c82',
                    fontFamily: 'Montserrat, sans-serif',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(0,60,130,0.08)';}}
                  onMouseLeave={(e) => {(e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'transparent';}}>

                  View Our Portfolio
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 900px) {
          .expertise-grid {
            grid-template-columns: 1fr !important;
            direction: ltr !important;
            gap: 40px !important;
          }
          .cta-grid {
            grid-template-columns: 1fr !important;
          }
        }
        .expertise-img:hover {
          transform: scale(1.03);
        }
        @media (max-width: 640px) {
          .expertise-grid > div > div > h2 {
            font-size: 28px !important;
          }
        }
      `}</style>
    </main>);

}